/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable } from "@angular/core";
import { BusEvent, EventBusService, Logger } from "@html-core";
import { ConnectedMessageService } from "../../../native-shared/base/connected-message.service";
import { Port } from "../../../chrome-client/base/model/rx-bridge-type";
import {
   RemoteSessionEventService,
   SessionMsg
} from "../../../shared/common/remote-session/remote-session-event.service";
import { DesktopBlastController } from "./desktop-blast-controller";

@Injectable()
export class SessionSyncService {
   private sessionKey;
   private desktopBlastController: DesktopBlastController;
   private pendingMessageParameters;

   constructor(
      private connectedMessageService: ConnectedMessageService,
      private remoteSessionEventService: RemoteSessionEventService,
      private eventBusService: EventBusService
   ) {
      window.addEventListener("beforeunload", () => {
         this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
            type: Port.SessionMsg.onWindowClosed
         });
      });
   }

   public init = (sessionKey, desktopBlastController) => {
      Logger.info(`SessionSyncService init with sessionKey: ${sessionKey}`);
      this.desktopBlastController = desktopBlastController;
      this.sessionKey = sessionKey;

      this.remoteSessionEventService.addEventListener(SessionMsg.SESSION_CONNECTING_MSG, this.onSessionConnecting);
      this.remoteSessionEventService.addEventListener(SessionMsg.SESSION_CONNECTED_MSG, this.onSessionConnected);
      this.remoteSessionEventService.addEventListener(SessionMsg.SESSION_DISCONNECTED_MSG, this.onSessionDisconnected);
      this.remoteSessionEventService.addEventListener(SessionMsg.SESSION_REMOVED_MSG, this.onSessionClosed);
      this.remoteSessionEventService.addEventListener(
         SessionMsg.SESSION_TOKEN_UPDATED_MSG,
         this.onReconnectTokenUpdated
      );
      this.remoteSessionEventService.addEventListener(SessionMsg.SESSION_ACTIVE_MSG, this.onSessionActive);
      this.remoteSessionEventService.addEventListener(SessionMsg.SESSION_MODAL_MSG, this.onSessionMessage);

      this.connectedMessageService.initConnection(sessionKey, Port.ChannelName.Session);
      this.connectedMessageService.onMessage(this.sessionKey, Port.ChannelName.Session, (msg) => {
         Logger.info(`SessionSyncService received message: ${JSON.stringify(msg)}`);
         if (msg.type === Port.SessionMsg.requestSessionDisconnect) {
            const wmksSession = this.desktopBlastController.getCurrentSession();
            if (wmksSession) {
               wmksSession.requestDisconnect();
            }
         } else if (msg.type === Port.SessionMsg.sessionMessageHandled) {
            this.onSessionMessageHandled();
         } else if (msg.type === Port.SessionMsg.restoreApplicationSession || msg.type === Port.SessionMsg.restoreApp) {
            this.eventBusService.dispatch(new BusEvent.RestoreAppRequest(msg.content.sessionId, msg.content.windowIds));
         } else if (msg.type === Port.SessionMsg.requestWindowClose) {
            this._closeWindow();
         }
      });
   };

   private onSessionConnecting = (wmksSession) => {
      this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
         type: Port.SessionMsg.onConnecting
      });
   };

   private onSessionConnected = (session) => {
      this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
         type: Port.SessionMsg.onConnected,
         content: {
            isApp: session.isApplicationSession,
            isWmksActive: false,
            isMultiSession: session.isMultiSession
         }
      });
   };

   private onSessionDisconnected = (session) => {
      setTimeout(() => {
         this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
            type: Port.SessionMsg.onDisconnected
         });
      });
   };

   private _closeWindow = () => {
      window.close();
   };

   private onSessionClosed = () => {
      setTimeout(() => {
         this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
            type: Port.SessionMsg.onClosed
         });
      });
   };

   private onReconnectTokenUpdated = (sessionKey, isApp, reconnectToken) => {
      this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
         type: Port.SessionMsg.onReconnectToken,
         content: reconnectToken
      });
   };

   private onSessionActive = (session) => {
      this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
         type: Port.SessionMsg.onSessionActive
      });
   };

   private onSessionMessage = (sessionKey, isApp, parameters) => {
      if (this.pendingMessageParameters) {
         return;
      }
      this.pendingMessageParameters = parameters;
      this.connectedMessageService.sendMessage(this.sessionKey, Port.ChannelName.Session, {
         type: Port.SessionMsg.sessionMessage,
         content: {
            type: parameters.type,
            data: parameters.data
         }
      });
   };

   private onSessionMessageHandled = (parameters?: any) => {
      if (!this.pendingMessageParameters) {
         return;
      }
      if (this.pendingMessageParameters.callback) {
         let messageResponseHandler = this.pendingMessageParameters.callback;
         this.pendingMessageParameters = null;
         messageResponseHandler();
      }
   };
}
